/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {Label} from "pages/Front/Booking/components/BookingInput";
import clsx from "clsx";
import {
    AtSymbolIcon,
    BuildingOffice2Icon,
    ChatBubbleBottomCenterTextIcon,
    EnvelopeOpenIcon,
    MapIcon,
    MapPinIcon,
    PhoneIcon,
    RectangleStackIcon,
    ShieldCheckIcon,
    UserIcon,
    UsersIcon,
} from "@heroicons/react/24/outline";
import {getAutoComplete} from "utils/autoCompleteUtils";

interface BookingLoginInputProps {
    register: any;
    name: string;
    label: string | "undefined";
    type?: string;
    id?: string;
    className?: string;
    placeholder?: string | number | null | any;
    required?: boolean | null;
    errors?: any;
    onEnter?: any;
    labelLess?: boolean;
    disabled?: boolean;
    showIcon?: boolean;
}

const BookingLoginInput = (props: BookingLoginInputProps) => {
    let type = "text";
    if (props.type !== undefined) {
        type = props.type;
    }
    const iconClass = "h-5 w-5 text-black text-opacity-30";
    const className = clsx(
        props.className,
        props.disabled && "grayscale bg-gray-400",
        type === "password" || type === "text" || type === "email" || type === "number" || type === "tel" ? "py-3 px-3 h-[50px]" : "p-1",
        " mt-0 border rounded-md focus:outline-none active:ring-cta-800 active:ring-2 active:ring-offset-2 focus:border-cta-800 checked:bg-cta-800 sm:text-sm",
        props.errors?.[props.name] ? "ring-2 ring-red-500 border-red-500" : "border-arbo-borderGray"
    );

    return (
        <div className={"BookingLoginInput relative w-full"}>
            <div className={"relative flex flex-row items-center space-x-2"}>
                <Label label={props.label} name={props.name} />
                {props.required && <span className={"font-bold text-red-500"}>* </span>}
            </div>
            <div className="mt-0 relative rounded-md shadow-sm">
                {props.showIcon && (
                    <div className="absolute inset-y-0 h-full left-0 pl-3 flex items-center pointer-events-none z-20">
                        {props.name === "company_name" && <BuildingOffice2Icon aria-hidden="true" className={iconClass} />}
                        {props.name === "cin" && <RectangleStackIcon aria-hidden="true" className={iconClass} />}
                        {props.name === "street_address" && <MapPinIcon aria-hidden="true" className={iconClass} />}
                        {props.name === "city" && <MapIcon aria-hidden="true" className={iconClass} />}
                        {props.name === "postal_code" && <EnvelopeOpenIcon aria-hidden="true" className={iconClass} />}
                        {props.name === "first_name" && <UserIcon aria-hidden="true" className={iconClass} />}
                        {props.name === "name" && <UserIcon aria-hidden="true" className={iconClass} />}
                        {props.name === "last_name" && <UsersIcon aria-hidden="true" className={iconClass} />}
                        {props.name === "email" && <AtSymbolIcon aria-hidden="true" className={iconClass} />}
                        {props.name === "phone" && <PhoneIcon aria-hidden="true" className={iconClass} />}
                        {props.name === "code" && <ShieldCheckIcon aria-hidden="true" className={iconClass} />}
                        {(props.name === "password" || props.name === "new_password" || props.name === "confirm_password") && (
                            <ShieldCheckIcon aria-hidden="true" className={iconClass} />
                        )}
                        {props.name === "message" && <ChatBubbleBottomCenterTextIcon aria-hidden="true" className={iconClass} />}
                    </div>
                )}
                <input
                    {...props.register(props.name)}
                    className={className}
                    disabled={props.disabled}
                    id={"input_" + props.name}
                    placeholder={props.placeholder}
                    name={props.name}
                    autoComplete={getAutoComplete(props.name)}
                    type={props.type || "text"}
                />
            </div>
        </div>
    );
};

export default BookingLoginInput;
